import { CheckCircle2 } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import BannerImage from "../../images/Frame1.svg";
import { tenant } from "../../tenant";
import { PublicPortalHeroBannerSkeleton } from "./PublicPortalSkeleton";

const HeroCopy = ({ hero, compact = false }) => (
  <div className={compact ? "max-w-full" : "max-w-[42%] xl:max-w-[38%]"}>
    <span
      className={`inline-flex rounded-full bg-cfSearchContainer/95 font-semibold text-cfPrimary ${
        compact
          ? "px-3 py-1 text-xs"
          : "px-3.5 py-1.5 text-[clamp(0.75rem,1.05vw,0.95rem)]"
      }`}
    >
      {hero.eyebrow}
    </span>
    <h1
      className={`font-bold leading-tight text-cfPrimary ${
        compact
          ? "mb-3 mt-3 text-3xl sm:text-4xl"
          : "mb-[0.6em] mt-[0.6em] text-[clamp(2rem,4.2vw,3.75rem)]"
      }`}
    >
      {hero.title}
    </h1>
    <p
      className={`text-cfGlobalText ${
        compact
          ? "mb-4 text-sm leading-6 sm:text-base sm:leading-7"
          : "mb-[1.1em] text-[clamp(0.95rem,1.35vw,1.2rem)] leading-[1.6]"
      }`}
    >
      {hero.description}
    </p>
    <ul className={`m-0 p-0 ${compact ? "space-y-2.5" : "space-y-[0.7em]"}`}>
      {(hero.benefits || []).map((benefit) => (
        <li
          className={`flex items-center gap-2.5 font-medium text-cfCardTagText ${
            compact
              ? "text-sm sm:text-base"
              : "text-[clamp(0.95rem,1.25vw,1.15rem)]"
          }`}
          key={benefit}
        >
          <CheckCircle2
            aria-hidden="true"
            className="shrink-0 text-cfPrimary"
            size={compact ? 18 : 22}
          />
          {benefit}
        </li>
      ))}
    </ul>
  </div>
);

const PublicPortalHero = ({ bannerImage, hero, isLoading = false }) => {
  const { t } = useTranslation("publicPortal");
  const branding = tenant.branding || {};
  const localDesktop = branding.portalBanner;
  const localMobile = branding.portalBannerMobile;
  const preferLocal = branding.preferLocalHeroBanners === true;
  const overlayHeroCopy = branding.overlayHeroCopy === true;

  const [desktopBanner, setDesktopBanner] = useState(
    () => (preferLocal && localDesktop) || localDesktop || bannerImage || BannerImage,
  );
  const [mobileBanner, setMobileBanner] = useState(
    () => (preferLocal && localMobile) || localMobile || bannerImage || BannerImage,
  );
  const [desktopBannerLoaded, setDesktopBannerLoaded] = useState(false);
  const [mobileBannerLoaded, setMobileBannerLoaded] = useState(false);

  useEffect(() => {
    // STP: always start from bundled banners (API CredIssuer art has baked-in titles).
    setDesktopBanner(
      preferLocal && localDesktop
        ? localDesktop
        : localDesktop || bannerImage || BannerImage,
    );
    setMobileBanner(
      preferLocal && localMobile
        ? localMobile
        : localMobile || bannerImage || BannerImage,
    );
    setDesktopBannerLoaded(false);
    setMobileBannerLoaded(false);
  }, [bannerImage, localDesktop, localMobile, preferLocal]);

  const handleDesktopBannerError = () => {
    setDesktopBanner((current) => {
      if (preferLocal && localDesktop && current === localDesktop && bannerImage) {
        return bannerImage;
      }
      return BannerImage;
    });
  };

  const handleMobileBannerError = () => {
    setMobileBanner((current) => {
      if (preferLocal && localMobile && current === localMobile && bannerImage) {
        return bannerImage;
      }
      return BannerImage;
    });
  };

  return (
    <>
      <section className="mb-8 w-full bg-cfSecondary px-5 py-8 sm:px-8 lg:hidden">
        <HeroCopy compact hero={hero} />
        <div className="relative mt-6 overflow-hidden">
          {(isLoading || !mobileBannerLoaded) && (
            <PublicPortalHeroBannerSkeleton />
          )}
          <img
            className={`mx-auto h-auto w-full max-w-md object-contain ${
              isLoading || !mobileBannerLoaded ? "hidden" : "block"
            }`}
            src={mobileBanner}
            onError={handleMobileBannerError}
            onLoad={() => setMobileBannerLoaded(true)}
            alt={t("accessibility.bannerAlt")}
          />
        </div>
      </section>

      <section className="relative mb-10 hidden w-full overflow-hidden bg-cfSecondary lg:block">
        {(isLoading || !desktopBannerLoaded) && (
          <div className="absolute inset-0 z-0">
            <PublicPortalHeroBannerSkeleton />
          </div>
        )}

        <img
          className={`block h-auto w-full ${
            isLoading || !desktopBannerLoaded ? "opacity-0" : "opacity-100"
          }`}
          src={desktopBanner}
          onError={handleDesktopBannerError}
          onLoad={() => setDesktopBannerLoaded(true)}
          alt=""
          aria-hidden="true"
        />

        {overlayHeroCopy && (
          <div className="absolute inset-0 z-10 flex items-center px-8 xl:px-12">
            <HeroCopy hero={hero} />
          </div>
        )}

        <span className="sr-only">{t("accessibility.bannerAlt")}</span>
      </section>
    </>
  );
};

export default PublicPortalHero;
